import 'normalize.css';
import '@shared/styles/vars.css';
import './index.css';
import '@shared/styles/print.css';

import { ApolloClient, ApolloLink, ApolloProvider } from '@apollo/client';
import { UIProvider } from '@ui/UIProvider';
import { useEffect, useState } from 'react';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import { setSharedApolloClient } from '@shared/api/client';
import { CardPaginationProvider } from '@shared/api/providers/cardPagination';
import { MBToastProvider } from '@shared/components/toast';
import { MBBillingProvider } from '@shared/scenes/billing/provider';
import { UpgradeModalProvider } from '@shared/scenes/billing/upgradeModal/provider';
import { PermissionError } from '@shared/scenes/error';
import { Provider, configureStore, fetchAndInsertUserIdIntoStore } from '@shared/store';
import { useDispatchViewportDimensions } from '@shared/store/useDispatchViewportDimensions';
import { GoogleMapsProvider } from '@shared/util/googleMaps/GoogleMapsContext';
import { ChannelPermissionsProvider } from '@shared/util/permissions/ChannelPermissionsProvider';
import { PrintProvider } from '@shared/util/print/PrintProvider';
import { createApolloClient } from './Client';
import { ExternalContent } from './ExternalContent';
import { useInitLaunchDarkly, useSetBusinessId } from './hooks';

function Hooks({ businessId }: { businessId: number }) {
  useDispatchViewportDimensions();
  useSetBusinessId(businessId);
  return null;
}

const safeAreaInsets = { top: 0, left: 0, right: 0, bottom: 0 };

export function App() {
  const params = new URLSearchParams(window.location.search);
  const templateSlug: string | null = getTemplateSlug(params);
  const businessId = Number(params.get('businessId'));
  const channelId = Number(params.get('channelId'));
  const cardId = params.get('cardId');
  const authToken = params.get('authToken');

  const [store, setStore] = useState<MBStore>();
  const [client, setClient] = useState<ApolloClient<any>>();

  useInitLaunchDarkly(businessId);

  useEffect(() => {
    const init = async () => {
      const newStore = configureStore();
      const newClient = createApolloClient([
        new ApolloLink((operation, forward) => {
          operation.setContext({
            headers: {
              // Note(fant): for legacy reasons, this is "WEB" | "IOS" | "ANDROID"
              'x-ts-platform': 'WEB',
              'x-coast-business-id': businessId,
              authorization: `Bearer ${authToken}`,
            },
          });
          return forward(operation);
        }),
      ]);

      setStore(newStore);
      setClient(newClient);
      setSharedApolloClient(newClient);

      await fetchAndInsertUserIdIntoStore(newStore.dispatch);
    };

    init();
  }, [authToken, businessId]);

  if (!store || !client) {
    return null;
  }

  if (!businessId || !channelId || !authToken) {
    return <PermissionError />;
  }

  return (
    <UIProvider>
      <SafeAreaInsetsContext.Provider value={safeAreaInsets}>
        <ApolloProvider client={client}>
          <GoogleMapsProvider>
            <Provider store={store}>
              <MBToastProvider>
                <ChannelPermissionsProvider channelId={channelId}>
                  <CardPaginationProvider>
                    <PrintProvider>
                      <UpgradeModalProvider>
                        <MBBillingProvider>
                          <ExternalContent
                            cardId={cardId}
                            channelId={channelId}
                            templateSlug={templateSlug ?? undefined}
                          />
                          <Hooks businessId={businessId} />
                        </MBBillingProvider>
                      </UpgradeModalProvider>
                    </PrintProvider>
                  </CardPaginationProvider>
                </ChannelPermissionsProvider>
              </MBToastProvider>
            </Provider>
          </GoogleMapsProvider>
        </ApolloProvider>
      </SafeAreaInsetsContext.Provider>
    </UIProvider>
  );
}
/**
 * We rely on the fact that the viewTemplateId === templateSlug for
 * all no-code workflows.  Eventually navigation would be specific
 * to viewTemplateId and templateSlug we be removed.
 *
 * In non no-code (low-code) workflows this parameter would be null
 */
function getTemplateSlug(params: URLSearchParams): string | null {
  return params.get('viewTemplateId');
}
